import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Linkedin, Globe, FileText } from 'lucide-react';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { post } from '@/lib/api';

interface Candidate {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  job_id: string;
  job_title: string;
  status: string;
  source: string;
  resume_url?: string;
  cover_letter_url?: string;
  linkedin_url?: string;
  portfolio_url?: string;
  tags: string[];
  notes?: string;
  rejection_reason?: string;
  created_at: string;
  interviews: Array<{ id: string; type: string; scheduled_at: string; interviewer_name: string; status: string; feedback?: string }>;
}

const STAGES = ['new', 'screening', 'interview', 'assessment', 'offer', 'hired', 'rejected', 'withdrawn'];

export function CandidateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: candidate, loading, refetch } = useApi<Candidate>(`candidates/${id}`);
  const [moving, setMoving] = useState(false);

  async function moveStage(status: string) {
    setMoving(true);
    try {
      const payload: Record<string, string> = { status };
      if (status === 'rejected') payload.rejection_reason = 'Not a fit for current role';
      await post(`candidates/${id}/move`, payload);
      refetch();
    } catch {
      // silent
    } finally {
      setMoving(false);
    }
  }

  if (loading) return <div className="text-muted-foreground">Loading...</div>;
  if (!candidate) return <div className="text-muted-foreground">Candidate not found</div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <button onClick={() => navigate('/candidates')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Back to Candidates
      </button>

      {/* Header */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 text-lg font-bold">
              {candidate.first_name[0]}{candidate.last_name[0]}
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">{candidate.first_name} {candidate.last_name}</h2>
              <p className="text-sm text-muted-foreground">{candidate.job_title || 'No position specified'}</p>
              <div className="flex items-center gap-3 mt-2">
                <StatusBadge status={candidate.status} />
                <span className="text-xs text-muted-foreground capitalize">Source: {candidate.source}</span>
                <span className="text-xs text-muted-foreground">Applied {candidate.created_at?.split('T')[0]}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline Stage Controls */}
      <Card>
        <h3 className="text-sm font-semibold text-foreground mb-3">Pipeline Stage</h3>
        <div className="flex flex-wrap gap-2">
          {STAGES.map(stage => (
            <button
              key={stage}
              onClick={() => void moveStage(stage)}
              disabled={moving || stage === candidate.status}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${
                stage === candidate.status
                  ? 'bg-amber-500 text-foreground'
                  : stage === 'rejected'
                  ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                  : 'bg-surface-3 text-muted-foreground hover:text-foreground hover:bg-surface-2'
              } disabled:opacity-50`}
            >
              {stage.replace('_', ' ')}
            </button>
          ))}
        </div>
        {candidate.rejection_reason && (
          <p className="mt-3 text-xs text-red-400">Rejection reason: {candidate.rejection_reason}</p>
        )}
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Contact Info */}
        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3">Contact Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-4 h-4" /> {candidate.email}</div>
            {candidate.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-4 h-4" /> {candidate.phone}</div>}
            {candidate.linkedin_url && (
              <a href={candidate.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-amber-500 hover:text-amber-400">
                <Linkedin className="w-4 h-4" /> LinkedIn Profile
              </a>
            )}
            {candidate.portfolio_url && (
              <a href={candidate.portfolio_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-amber-500 hover:text-amber-400">
                <Globe className="w-4 h-4" /> Portfolio
              </a>
            )}
          </div>
        </Card>

        {/* Documents */}
        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3">Documents</h3>
          <div className="space-y-2 text-sm">
            {candidate.resume_url ? (
              <a href={candidate.resume_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-amber-500 hover:text-amber-400">
                <FileText className="w-4 h-4" /> Resume
              </a>
            ) : (
              <p className="text-xs text-muted-foreground">No resume uploaded</p>
            )}
            {candidate.cover_letter_url && (
              <a href={candidate.cover_letter_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-amber-500 hover:text-amber-400">
                <FileText className="w-4 h-4" /> Cover Letter
              </a>
            )}
          </div>
        </Card>

        {/* Tags */}
        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {(candidate.tags ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No tags</p>
            ) : (
              candidate.tags.map(tag => (
                <span key={tag} className="px-2 py-1 bg-surface-3 rounded-md text-xs text-foreground">{tag}</span>
              ))
            )}
          </div>
        </Card>

        {/* Notes */}
        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3">Notes</h3>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{candidate.notes || 'No notes'}</p>
        </Card>
      </div>

      {/* Interviews */}
      {(candidate.interviews ?? []).length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-3">Interviews</h3>
          <div className="space-y-3">
            {candidate.interviews.map(interview => (
              <div key={interview.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div>
                  <p className="text-sm text-foreground capitalize">{interview.type.replace('_', ' ')}</p>
                  <p className="text-xs text-muted-foreground">with {interview.interviewer_name} - {interview.scheduled_at?.split('T')[0]}</p>
                </div>
                <StatusBadge status={interview.status} />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
